import type { TFlagSnapshot } from "./types";

let snapshot: TFlagSnapshot = {};
const listeners = new Set<() => void>();

export const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const setSnapshot = (next: TFlagSnapshot): void => {
  snapshot = next;
  listeners.forEach((listener) => listener());
};

/**
 * Assign the snapshot WITHOUT notifying subscribers.
 *
 * `FeatureFlagsProvider` seeds the React context synchronously, during its
 * first render, from the cached config (provider.tsx). This writes that same
 * value into the module-level snapshot, so the hook-free readers
 * (`isOnFeatureFlag`, `getSnapshot`, `window.__FEATURE_FLAGS__`) cannot
 * disagree with `useFeatureFlag` about a flag the cache can already answer.
 * Without it the module snapshot stays `{}` until the async resolve lands —
 * a sha256 of the org id plus a CDN fetch later — so for the whole of that
 * startup window a returning user's live flag (COLLABORATION,
 * TABLES_2_0_IN_CELL_DND) reads true through the context and false through
 * the module.
 *
 * Subscribers are deliberately NOT notified: this runs in the render phase,
 * where calling out into listeners would make the render impure. Nothing is
 * missed — the only subscriber reads `getSnapshot()` once when it subscribes
 * (features/analytics/hooks/use-register-feature-flags.tsx), and its effect
 * runs after this render, so it observes the seed. The provider's fetch
 * effect then calls `setSnapshot` with the fresh resolve, which does notify.
 */
export const seedSnapshot = (next: TFlagSnapshot): void => {
  snapshot = next;
};

export const getSnapshot = (): TFlagSnapshot => snapshot;

export const isOnFeatureFlag = (name: string): boolean =>
  snapshot[name]?.on ?? false;

export const getFeatureFlags = (): Record<string, boolean> => {
  const result: Record<string, boolean> = {};
  for (const key of Object.keys(snapshot)) {
    result[key] = snapshot[key].on;
  }
  return result;
};

declare global {
  interface Window {
    __FEATURE_FLAGS__: {
      getFeatureFlags: () => Record<string, boolean>;
      isOnFeatureFlag: (name: string) => boolean;
    };
  }
}

window.__FEATURE_FLAGS__ = { getFeatureFlags, isOnFeatureFlag };
